import React, { useEffect, useState } from "react";
import { Row, Container, Col, Card, CardHeader, CardBody } from "reactstrap";
import { useTranslation } from "react-i18next";
import SearchInput from "../Common/SearchInput";
import { useRecoilState } from "recoil";
import useUrlState from "@ahooksjs/use-url-state";
import { resetFormPromptState } from "src/recoil/atoms/global";
import { PAGINATION_DEFAULT } from "src/constant/common";
import { CustomLink } from "../Common/CustomLink";
import APP_ROUTES from "src/routes/routes";
import { handleScrollToTop } from "src/utils/common";
import TableCommon from "../Common/Table";
import { columns } from "./column";
import FormSelect from "../Common/Form/FormSelect";
import { useForm } from "react-hook-form";
import useGetTestPackageList from "./useGetTestPackageList";
import ModalManageTestSequenceTag from "../Common/Modal/ModalManageTestSequenceTag";
import useGetProjectInstanceList from "../ProjectInstance/useGetProjectInstanceList";
import ProtectedComponent from "../Permission";

const FORM_NAME = {
  CURRENT_PROJECT_INSTANCE: "currentProjectInstance",
};

const TestPackageList = () => {
  const { t } = useTranslation(["test-package", "common"]);

  const [resetForm, setResetForm] = useRecoilState(resetFormPromptState);
  const [selectedTestPackage, setSelectedTestPackage] = useState(null);
  const [isTagManageModalOpen, setIsTagManageModalOpen] = useState(false);
  const [isLoadingProjectInstance, setIsLoadingProjectInstance] = useState(true);
  const [isLoadingTestPackage, setIsLoadingTestPackage] = useState(true);
  const [params, setParams] = useUrlState(
    {},
    {
      navigateMode: "replace",
      stringifyOptions: {
        skipNull: true,
        skipEmptyString: true,
      },
    }
  );

  const [textSearch, setTextSearch] = useState(params?.condition || null);

  const { projectInstanceList: instanceOptions } = useGetProjectInstanceList(
    { isSelectOptions: true, refetchInterval: false, refetchOnWindowFocus: false },
    () => setIsLoadingProjectInstance(false)
  );
  const { testPackageList, testPackageListRefetch } = useGetTestPackageList(
    {
      page: params.page,
      limit: PAGINATION_DEFAULT.pageSize,
      q: params?.condition,
      yamcs_package_id: params?.instanceId,
      id: params?.id,
    },
    () => setIsLoadingTestPackage(false)
  );

  const { control, setValue } = useForm({
    defaultValues: {
      [FORM_NAME.CURRENT_PROJECT_INSTANCE]: params?.instanceId || null,
    },
  });

  const onKeyDownInput = (e) => {
    if (e.key === "Enter") {
      onSubmitSearch(e.target.value);
    }
  };

  const onSubmitSearch = (textSearch) => {
    if (params?.condition === textSearch && !isLoadingTestPackage) {
      setIsLoadingTestPackage(true);
    }
    setParams({
      page: 1,
      condition: textSearch,
    });
  };

  const handleManageTestSequenceTagClick = (instanceData) => {
    setSelectedTestPackage(instanceData);
    setIsTagManageModalOpen(true);
  };

  const handleChangeProjectInstance = (selectOption) => {
    setParams({
      ...params,
      page: 1,
      instanceId: selectOption || null,
    });
  };

  const getNewInstanceUrl = (page, condition, instanceId) => {
    const params = new URLSearchParams();

    if (page) params.set("page", page);
    if (condition) params.set("condition", condition);
    if (instanceId) params.set("instanceId", instanceId);

    const queryString = params.toString();
    return `${APP_ROUTES.TEST_PACKAGE_NEW}${queryString ? "?" + queryString : ""}`;
  };

  const redirectToSequenceList = (testPackageData) => {
    if (!testPackageData?.id) return;

    const baseUrl = `${APP_ROUTES.SEQUENCES}?projectId=${params?.projectId}`;
    const queryParams = [];

    if (testPackageData?.yamcs_package) {
      queryParams.push(`instanceId=${testPackageData.yamcs_package?.id}`);
    }

    if (testPackageData?.id) {
      queryParams.push(`testPackageId=${testPackageData.id}`);
    }

    const url = queryParams.length > 0 ? `${baseUrl}&${queryParams.join("&")}` : baseUrl;

    window.open(url, "_blank");
  };

  const handleCloseModal = () => {
    setIsTagManageModalOpen(false);
    setSelectedTestPackage(null);
  };

  const getCurrentPage = () => {
    return (params?.page || PAGINATION_DEFAULT.page) - 1;
  };

  const onChangePage = (value) => {
    setParams({
      ...params,
      page: value,
    });
    handleScrollToTop();
  };

  const handleSaveTagSuccess = async () => {
    handleScrollToTop();

    setParams({
      ...params,
      page: 1,
    });

    if (Number(params?.page) === 1 || params?.page === undefined) {
      setIsLoadingTestPackage(true);
      await testPackageListRefetch();
    }
  };

  useEffect(() => {
    setIsLoadingTestPackage(true);
  }, [params]);

  useEffect(() => {
    if (resetForm) {
      setTextSearch(null);
      setResetForm(false);
      handleScrollToTop();
    }
  }, [resetForm]);

  useEffect(() => {
    if (!params?.instanceId) {
      setValue(FORM_NAME.CURRENT_PROJECT_INSTANCE, null);
    }
  }, [params]);

  return (
    <Container fluid>
      <Row>
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h2 className="header-title mb-0">{t("list.title")}</h2>
                <SearchInput
                  submit={onSubmitSearch}
                  setTextSearch={setTextSearch}
                  onKeyDownInput={onKeyDownInput}
                  textSearch={textSearch}
                  placeholder={t("list.search_placeholder")}
                />
              </div>
              <Row>
                <Col md={12}>
                  <Card>
                    <CardHeader className="bg-white border-0 d-flex align-items-center justify-content-between">
                      <FormSelect
                        name={FORM_NAME.CURRENT_PROJECT_INSTANCE}
                        classGroup="mb-0 width-by-px-300"
                        control={control}
                        placeholder={t("list.filter_by_project_instance_placeholder")}
                        isSearchable
                        onChangeExtra={handleChangeProjectInstance}
                        isClearable
                        options={instanceOptions}
                        isLoading={isLoadingProjectInstance}
                      />
                      <ProtectedComponent id="test_package_create_new">
                        <CustomLink
                          to={getNewInstanceUrl(params?.page, params?.condition, params?.instanceId)}
                          className="btn btn-primary text-capitalize"
                        >
                          {t("list.redirect_new")}
                        </CustomLink>
                      </ProtectedComponent>
                    </CardHeader>
                    <CardBody className="bg-white">
                      <TableCommon
                        loading={isLoadingTestPackage}
                        columns={columns({ t, handleManageTestSequenceTagClick, redirectToSequenceList, params })}
                        data={testPackageList?.items || []}
                        onChangePage={onChangePage}
                        tableFixed
                        tableClassName="table striped table-collapse table-fixed"
                        customClassName="overflow-auto simulation-table"
                        pageCurrent={getCurrentPage()}
                        pageCount={testPackageList?.pagination?.total_page}
                        totalItem={testPackageList?.pagination?.total}
                        pageSize={PAGINATION_DEFAULT.pageSize}
                      />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Row>

      {isTagManageModalOpen && (
        <ModalManageTestSequenceTag
          testPackageData={selectedTestPackage}
          isOpen={isTagManageModalOpen}
          toggle={handleCloseModal}
          onSaveSuccess={handleSaveTagSuccess}
        />
      )}
    </Container>
  );
};

export default TestPackageList;
